import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Row, Col,
} from 'reactstrap';
import { getPlansForSpecialty, getSpecialtyFields } from 'config/subscriptionPlans';
import { updateTrialPlan, billingActionReset } from 'redux/Billing/BillingActions';
import { getUserData } from 'redux/UserAuthentication/LoginActions';

const ChangeTrialPlanModal = ({ isOpen, toggle, currentPlanId, userDetails, activeSpecialty = 'pmr' }) => {
    const dispatch = useDispatch();
    const [selected, setSelected] = useState(currentPlanId || '');
    const [loading, setLoading] = useState(false);
    const specialtyPlans = getPlansForSpecialty(activeSpecialty);

    useEffect(() => {
        if (isOpen) setSelected(currentPlanId || '');
    }, [isOpen, currentPlanId]);

    const fields = getSpecialtyFields(activeSpecialty);
    const filteredPlans = specialtyPlans.filter(p => {
        if (activeSpecialty === 'pmr' && p.id.endsWith('48Months339')) {
            return userDetails?.[fields.year] === 'PGY 1';
        }
        return true;
    });

    const handleSubmit = async () => {
        if (!selected) {
            Swal.fire('Please select a plan', '', 'warning');
            return;
        }
        if (selected === currentPlanId) {
            toggle();
            return;
        }
        setLoading(true);
        try {
            await dispatch(updateTrialPlan(selected));
            await dispatch(getUserData({ onLogin: true }));
            dispatch(billingActionReset());
            Swal.fire({ title: 'Plan Updated', text: 'Your trial plan has been updated.', icon: 'success', timer: 2000 });
            toggle();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to update plan', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getBtnClass = (plan, isSelected) => {
        return isSelected ? `btn-${plan.color}` : `btn-outline-${plan.color}`;
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>Change Trial Plan</ModalHeader>
            <ModalBody>
                <p className="text-muted mb-3">
                    Your card will be charged for the plan you select here after your 14-day free trial ends.
                </p>
                <Row>
                    {filteredPlans.map(plan => (
                        <Col xs="6" md="3" className="mb-3" key={plan.id}>
                            <button
                                type="button"
                                className={`btn ${getBtnClass(plan, selected === plan.id)} w-100`}
                                onClick={() => setSelected(plan.id)}
                                style={{ fontSize: '0.82rem', padding: '12px 6px', minHeight: 70 }}
                            >
                                <div><strong>{plan.months} mo</strong></div>
                                <div>${plan.price}</div>
                                {plan.popular && <span className="badge badge-warning mt-1">Best</span>}
                            </button>
                        </Col>
                    ))}
                </Row>
                {selected && (
                    <div className="text-center mt-2">
                        <small className="text-muted">
                            After your 14-day free trial, your card will be charged{' '}
                            <strong>${specialtyPlans.find(p => p.id === selected)?.price}</strong> for{' '}
                            <strong>{specialtyPlans.find(p => p.id === selected)?.months} months</strong> of access.
                        </small>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={loading}>Cancel</Button>
                <Button color="primary" onClick={handleSubmit} disabled={loading || !selected}>
                    {loading ? 'Saving...' : 'Save Plan'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ChangeTrialPlanModal;
